import { useState, useEffect, useMemo, ReactElement, ReactNode } from 'react'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Table, useMagneticTable, MdsTableColumn, getSelectionColumn } from '@magnetic/table'
import { FilterBar } from '@magnetic/filter-bar'
import { Search } from '@magnetic/search'
import { Select } from '@magnetic/select'
import { Flex } from '@magnetic/flex'
import { Heading } from '@magnetic/heading'
import { Text } from '@magnetic/text'
import { Tag } from '@magnetic/tag'
import { timestampToDate } from '@/lib/protobuf-utils'
import { Spinner } from '@magnetic/spinner'
import { User, Users, XCircle } from '@/lib/icons'
import type { ListUsersResponse, UserInfo } from '@/gen/catpocs/v1/admin_pb'
import type { listUsers } from '@/lib/api'

interface UserRow {
  id: string
  email: string
  name: string
  lastSignIn: string
  totalSignIns: number
  projectsCount: number
  activeSessions: number
  isSuperAdmin: boolean
  createdAt: string
}

interface UsersTableProps {
  queryFn: typeof listUsers
  title?: string
  description?: string
  selectableRows?: boolean
  configComponents?: ReactElement | null
  bulkActions?: (selectedCount: number, selectedRows: UserRow[]) => ReactNode
  initialPageSize?: number
}

export function UsersTable({
  queryFn,
  title = 'Users',
  description,
  selectableRows = false,
  configComponents,
  bulkActions,
  initialPageSize = 20,
}: UsersTableProps) {
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [filterType, setFilterType] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(0)

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setCurrentPage(0) // Reset to first page on search
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Fetch data with all filters
  const pageSize = initialPageSize
  const skip = currentPage * pageSize
  const { data: rawData, isLoading, error } = useConnectQuery(
    queryFn,
    {
      search: debouncedSearch,
      filter: filterType,
      pageSize: pageSize,
      skip: skip,
    },
    {
      refetchInterval: 60000, // Refresh every minute
    }
  )

  const data = rawData as ListUsersResponse | undefined
  const totalCount = data?.totalCount ?? 0

  // Transform data for table
  const tableData: UserRow[] = useMemo(() => {
    const users: UserInfo[] = data?.users ?? []
    if (users.length === 0) return []
    return users.map((user) => ({
      id: user.id || '',
      email: user.email || '',
      name: user.name || '',
      lastSignIn: user.lastSignIn ? (timestampToDate(user.lastSignIn)?.toLocaleString() || 'Invalid Date') : 'Never',
      totalSignIns: user.totalSignIns || 0,
      projectsCount: user.projectsCount || 0,
      activeSessions: user.activeSessions || 0,
      isSuperAdmin: user.isSuperAdmin || false,
      createdAt: user.createdAt ? (timestampToDate(user.createdAt)?.toLocaleString() || 'Invalid Date') : '',
    }))
  }, [data])

  // Define columns
  const columns: MdsTableColumn<UserRow>[] = useMemo(() => {
    const cols: MdsTableColumn<UserRow>[] = [
      {
        accessorKey: 'name',
        header: 'Name',
        cell: (info) => (
          <Flex align="center" gap={8}>
            <User size={16} weight="bold" />
            <Flex direction="vertical" gap={2}>
              <Text size="p3" weight="semi-bold">{info.getValue() as string}</Text>
              <Text size="p4" color="light">{info.row.original.email}</Text>
            </Flex>
          </Flex>
        ),
      },
      {
        accessorKey: 'lastSignIn',
        header: 'Last Sign In',
        cell: (info) => <Text size="p3">{info.getValue() as string}</Text>,
      },
      {
        accessorKey: 'totalSignIns',
        header: 'Total Sign Ins',
        cell: (info) => <Text size="p3">{info.getValue() as number}</Text>,
      },
      {
        accessorKey: 'projectsCount',
        header: 'Projects',
        cell: (info) => <Text size="p3">{info.getValue() as number}</Text>,
      },
      {
        accessorKey: 'activeSessions',
        header: 'Active Sessions',
        cell: (info) => {
          const count = info.getValue() as number
          return (
            <Text size="p3" weight="semi-bold" style={{
              color: count > 0 ? 'var(--mds-color-theme-text-positive-normal)' : 'var(--mds-color-theme-text-normal)'
            }}>
              {count.toString()}
            </Text>
          )
        },
      },
      {
        accessorKey: 'isSuperAdmin',
        header: 'Role',
        cell: (info) => {
          const isSuperAdmin = info.getValue() as boolean
          return isSuperAdmin ? (
            <Tag color="lavender">Super Admin</Tag>
          ) : (
            <Text size="p3" color="light">User</Text>
          )
        },
      },
    ]

    if (selectableRows) {
      return [getSelectionColumn<UserRow>(), ...cols]
    }
    return cols
  }, [selectableRows])

  // Use Magnetic table hook
  const { table } = useMagneticTable({
    data: tableData,
    columns,
  })

  const showReset = debouncedSearch !== '' || filterType !== ''
  const handleReset = () => {
    setSearchTerm('')
    setDebouncedSearch('')
    setFilterType('')
    setCurrentPage(0)
  }

  if (error) {
    return (
      <Flex direction="vertical" align="center" gap={16} style={{ padding: 48 }}>
        <XCircle size={48} />
        <Heading size="sub-section">Failed to load users</Heading>
        <Text color="light">{error instanceof Error ? error.message : 'An error occurred'}</Text>
      </Flex>
    )
  }

  return (
    <Flex direction="vertical" gap={16} style={{ width: '100%' }}>
      {(title || description) && (
        <Flex direction="vertical" gap={4}>
          {title && <Heading size="h2">{title}</Heading>}
          {description && <Text color="light">{description}</Text>}
        </Flex>
      )}

      <FilterBar
        searchBar={
          <Search
            label="Search users"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        }
        results={totalCount}
        isLoadingResults={isLoading}
        showResetButton={showReset}
        onReset={handleReset}
        configComponents={configComponents ?? undefined}
      >
        <Flex style={{ minWidth: '180px' }}>
          <Select
            placeholder="Filter by"
            value={filterType}
            onChange={(value) => {
              setFilterType(value as string)
              setCurrentPage(0)
            }}
            options={[
              { label: 'All Users', value: '' },
              { label: 'Super Admins', value: 'super_admin' },
              { label: 'Active (30 days)', value: 'active' },
              { label: 'Inactive', value: 'inactive' },
            ]}
          />
        </Flex>
      </FilterBar>

      {selectableRows && bulkActions && table.getSelectedRowModel().rows.length > 0 && (
        <Table.BulkActionBar>
          <Flex align="center" gap={12}>
            {bulkActions(
              table.getSelectedRowModel().rows.length,
              table.getSelectedRowModel().rows.map((row) => row.original)
            )}
          </Flex>
        </Table.BulkActionBar>
      )}

      {isLoading ? (
        <Flex align="center" justify="center" style={{ padding: 48 }}>
          <Spinner size="lg" />
        </Flex>
      ) : tableData.length === 0 ? (
        <Flex direction="vertical" align="center" gap={16} style={{ padding: 48 }}>
          <Users size={48} style={{ opacity: 0.3 }} />
          <Heading size="sub-section">No users found</Heading>
          <Text color="light">Try adjusting your search or filters</Text>
        </Flex>
      ) : (
        <Table
          table={table}
          paginationControls={totalCount > pageSize ? {} : undefined}
        />
      )}
    </Flex>
  )
}
